import { useAxios } from "@vueuse/integrations/useAxios";
import type { Ref } from "vue";
import type { CompleteParam, SelectParam } from "../common/types.common";
import { useEnvironmentStore } from "../stores/environment.store";
import { useTokenStore } from "../stores/token.store";
import { RequestController } from "./controller";

function buildURI(uri: string) {
    const env = useEnvironmentStore();
    return `${env.api}${uri}`;
}

export function get<Res = never>(
    uri: string,
    { authenticated }: { authenticated: boolean } = { authenticated: true },
) {
    let token: string | undefined;
    if (authenticated) {
        token = useTokenStore().token;
    }

    const { data, execute } = useAxios(
        buildURI(uri),
        {
            method: "GET",
            headers: {
                Authorization: token,
            },
        },
        { immediate: false },
    );

    return new RequestController<Res>(data, execute).clearHooks();
}

export function post<Req = never, Res = unknown>(
    uri: string,
    payload: NoInfer<Req>,
    { authenticated }: { authenticated: boolean } = { authenticated: true },
) {
    let token: string | undefined;
    if (authenticated) {
        token = useTokenStore().token;
    }

    const { data, execute } = useAxios(
        buildURI(uri),
        {
            method: "POST",
            headers: {
                Authorization: token,
            },
            data: payload,
        },
        { immediate: false },
    );

    return new RequestController<Res>(data, execute);
}

export function postEmpty<Res = unknown>(
    uri: string,
    options: { authenticated: boolean } = { authenticated: true },
) {
    return post<object, Res>(uri, {}, options);
}

export function download(
    uri: string,
    { authenticated }: { authenticated: boolean } = { authenticated: true },
) {
    let token: string | undefined;
    if (authenticated) {
        token = useTokenStore().token;
    }

    const { data, execute } = useAxios(
        buildURI(uri),
        {
            method: "GET",
            headers: {
                Authorization: token,
            },
            responseType: "blob",
        },
        { immediate: false },
    );

    return { data: data, execute: execute };
}

export async function asUpdateHook<T>(
    req: RequestController<T>,
    isLoading: Ref<boolean> | undefined,
) {
    return (await req.standardHooks().execute(isLoading)) !== undefined;
}

export async function asRemoveHook<T>(
    req: RequestController<T>,
    isLoading: Ref<boolean> | undefined,
) {
    return (await req.standardHooks().execute(isLoading)) !== undefined;
}

export function buildSelectParam(param?: SelectParam) {
    if (param === undefined) return "";

    const params = new URLSearchParams();
    if (param.search !== undefined) {
        params.append("search", param.search);
    }
    if (param.current !== undefined && param.current.length > 0) {
        params.append("current", param.current);
    }

    return "?" + params.toString();
}

export function buildCompleteParam(param?: CompleteParam) {
    if (param === undefined) return "";

    const params = new URLSearchParams();
    if (param.query !== undefined) {
        params.append("query", param.query);
    }

    return "?" + params.toString();
}
